import { ZoneContextManager } from '@opentelemetry/context-zone'
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-proto'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { Resource } from '@opentelemetry/resources'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'

import env from '../../../env'
import { DEFAULT_EXPORT_TIMEOUT, DEFAULT_MAX_EXPORT_BATCH_SIZE, DEFAULT_MAX_QUEUE_SIZE, DEFAULT_SCHEDULE_DELAY } from '../constants'
import { normalizePathname } from '../tracking/utils/normalizePathname'

export const setupTracerProvider = (resource: Resource) => {
    const exporter = new OTLPTraceExporter({ url: env.VITE_OTEL_TRACE_ENDPOINT })

    const batchProcessor = new BatchSpanProcessor(exporter, {
        maxExportBatchSize: env.VITE_OTEL_BSP_MAX_EXPORT_BATCH_SIZE ?? DEFAULT_MAX_EXPORT_BATCH_SIZE,
        maxQueueSize: env.VITE_OTEL_BSP_MAX_QUEUE_SIZE ?? DEFAULT_MAX_QUEUE_SIZE,
        scheduledDelayMillis: env.VITE_OTEL_BSP_SCHEDULE_DELAY ?? DEFAULT_SCHEDULE_DELAY,
        exportTimeoutMillis: env.VITE_OTEL_BSP_EXPORT_TIMEOUT ?? DEFAULT_EXPORT_TIMEOUT,
    })

    const tracerProvider = new WebTracerProvider({
        resource: resource,
        spanProcessors: [batchProcessor],
    })

    tracerProvider.register({
        contextManager: new ZoneContextManager(),
        propagator: new CompositePropagator({
            propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
        }),
    })
}

export function urlToRegex(url: string): RegExp {
    return new RegExp(`^${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)
}

const tracePropagationTargets: RegExp[] = [urlToRegex(env.VITE_API_BASE_URL), urlToRegex(env.VITE_DIPS_BASE_URL)]

export const setupTraceInstrumentations = () => {
    registerInstrumentations({
        instrumentations: [
            new DocumentLoadInstrumentation(),
            new FetchInstrumentation({
                propagateTraceHeaderCorsUrls: tracePropagationTargets,
                applyCustomAttributesOnSpan: (span, request) => {
                    if (request instanceof Request) {
                        // Improve span name from the default "HTTP <method>" to "<method> <url pathname>", e.g. "GET /api/v4/surgery/scheduled",
                        // and normalize the pathname to still get low cardinality in Otel for easier grouping.
                        span.updateName(`${request.method} ${normalizePathname(request.url)}`)

                        // Add missing pathname (i.e. not normalized) as attribute for finding specific traces when needed
                        const { pathname } = new URL(request.url)
                        span.setAttribute('di.http.pathname', pathname)
                    }
                },
            }),
            new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls: tracePropagationTargets }),
        ],
    })
}
